import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import userAPI from "../../api/UserApi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function OrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState({});
  const [orderItems, setOrderItems] = useState([]);

  if (!id) {
    navigate("/");
  }

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const res = await userAPI.orderDetails(id);
        if (res.code === 200) {
          setOrder(res.data);
          setOrderItems(res.data?.items || []);
        }
      } catch (err) {}
    };

    fetchOrderDetails();
  }, [id]);

  const handleDownload = async (orderId) => {
    const element = document.querySelector(".invoice-wrapper");

    // Scroll to top if needed before capturing
    window.scrollTo(0, 0);

    // Wait for a short time to ensure images are fully loaded
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const canvas = await html2canvas(element, {
      scale: 1,
      useCORS: true, // 👈 Important for external images
      allowTaint: false,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pageWidth;
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    // Fit to one page (or multiple if needed)
    if (pdfHeight > pageHeight) {
      let position = 0;
      while (position < pdfHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);
        position += pageHeight;
        if (position < pdfHeight) pdf.addPage();
      }
    } else {
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    }

    pdf.save(orderId + ".pdf");
  };

  return (
    <>
      <div className="container-fluid py-4">
        <div className="d-flex justify-content-end gap-2 mb-3">
          <button
            className="btn btn-outline-dark px-4 rounded-0"
            onClick={() => handleDownload(order?.orderId)}
          >
            Download Invoice
          </button>

          <button className="btn btn-dark px-4 rounded-0">
            Track Shipment
          </button>
        </div>

        <div className="invoice-wrapper">
          <div className="row g-4">
            {/* RIGHT SECTION */}
            <div className="col-12">
              {/* ORDER INFO */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white fw-bold py-2">
                  Order Information
                </div>

                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <small className="text-muted">Order ID</small>
                      <h6 className="fw-bold">#{order?.orderId}</h6>
                    </div>

                    <div className="col-md-4 mb-3">
                      <small className="text-muted">Order Date</small>
                      <h6 className="fw-bold">
                        {new Date(order?.orderDate).toLocaleString()}
                      </h6>
                    </div>

                    <div className="col-md-4 mb-3">
                      <small className="text-muted">Total Amount</small>
                      <h6 className="fw-bold">₹{order?.total}/-</h6>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Order Status</small>
                      <div>
                        <span className="badge bg-primary">
                          {order?.payment?.status}
                        </span>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Payment Status</small>
                      <div>
                        <span className="badge bg-success">
                          {order?.payment?.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ITEMS */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white fw-bold py-2">
                  Item's Information
                </div>

                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Item</th>
                        <th>MRP</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>

                    <tbody>
                      {orderItems?.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={item?.thumbnail}
                                alt=""
                                width={50}
                                className="me-3 rounded"
                              />

                              <div>
                                <h6 className="mb-0">{item?.productName}</h6>

                                <small className="text-muted">
                                  {item?.variantName && (
                                    <small className="text-muted">
                                      {item?.variantName}
                                    </small>
                                  )}
                                </small>
                              </div>
                            </div>
                          </td>

                          <td>₹{item?.mrp}/-</td>

                          <td>₹{item?.price}/-</td>

                          <td>{item?.quantity}</td>

                          <td className="text-end">
                            ₹{item?.price * item?.quantity}/-
                          </td>
                        </tr>
                      ))}

                      <tr>
                        <td colSpan={4} className="text-end fw-bold">
                          Subtotal
                        </td>

                        <td className="text-end fw-bold">₹{order?.subtotal}</td>
                      </tr>

                      <tr>
                        <td colSpan={4} className="text-end fw-bold">
                          Total
                        </td>

                        <td className="text-end fw-bold">₹{order?.total}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* PAYMENT */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white fw-bold py-3">
                  Payment Details
                </div>

                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-4">
                      <small className="text-muted">Payment Type</small>
                      <h6>{order?.payment?.method}</h6>
                    </div>

                    <div className="col-md-4 mb-4">
                      <small className="text-muted">Payment Status</small>
                      <h6>{order?.payment?.status}</h6>
                    </div>

                    <div className="col-md-4 mb-4">
                      <small className="text-muted">Payment Date & Time</small>
                      <h6>
                        {new Date(order?.payment?.paidAt).toLocaleString()}
                      </h6>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Gateway Order Id</small>
                      <h6>{order?.payment?.details?.gatewayOrderId}</h6>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Payment Id</small>
                      <h6>{order?.payment?.details?.id}</h6>
                    </div>

                    <div className="col-md-4">
                      <small className="text-muted">Payment Method</small>
                      <h6>{order?.payment?.details?.method}</h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              {/* CUSTOMER INFO */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white fw-bold py-2">
                  Customer Information
                </div>

                <div className="card-body info-card-body">
                  <div className="info-row">
                    <span>Name</span>
                    <strong>
                      {order?.user?.firstName} {order?.user?.lastName}
                    </strong>
                  </div>

                  <div className="info-row">
                    <span>Email</span>
                    <strong>{order?.user?.email}</strong>
                  </div>

                  <div className="info-row">
                    <span>Phone</span>
                    <strong>{order?.user?.mobile}</strong>
                  </div>

                  <div className="info-row">
                    <span>Address</span>
                    <strong>
                      {[
                        order?.address?.addressLine1,
                        order?.address?.addressLine2,
                        order?.address?.district,
                        order?.address?.state,
                        order?.address?.pinCode,
                        order?.address?.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OrderDetails;
